/**
 * Closing a Base UI popup and handing focus ON, rather than letting it be restored to the trigger.
 *
 * Base UI returns focus to the trigger when a popup closes, and it does so a BEAT LATE — after the
 * thing this close was making room for has already taken focus itself. The trainer is then standing
 * on the trigger of a popup that is gone, beside the overlay they just opened. `finalFocus`
 * returning `false` is Base UI's documented "do not move focus", and it runs at exactly the moment
 * the restore would otherwise happen, so nothing races it.
 *
 * Where the hand-over has a destination of its own — a menu closing onto the item that holds a
 * keyboard lift's arrow keys — that element is focused HERE and `false` returned. Returning it
 * instead does not work: Base UI (1.6) passes a returned element on to its first TABBABLE
 * descendant, and an element focusable by script only (`tabIndex={-1}`) hands the keys to the first
 * button inside it.
 *
 * Every other close is untouched: `finalFocus` answers `true`, which is "carry on as usual".
 */

import { useRef, type RefObject } from 'react';

export interface FocusHandOff {
  /**
   * Arm the hand-over for the close about to happen. `to` is focused at the moment the restore
   * would have run; omit it where whatever is being opened takes focus for itself.
   */
  readonly handOff: (to?: RefObject<HTMLElement | null>) => void;
  /** Bind to the popup's `finalFocus`. */
  readonly finalFocus: () => boolean;
}

export function useFocusHandOff(): FocusHandOff {
  const armed = useRef(false);
  const target = useRef<RefObject<HTMLElement | null> | null>(null);

  return {
    handOff: (to) => {
      armed.current = true;
      target.current = to ?? null;
    },
    finalFocus: () => {
      if (!armed.current) return true;
      armed.current = false;
      target.current?.current?.focus();
      target.current = null;
      return false;
    },
  };
}
